import { ButtonLink, Stack } from 'component-library';
import { Trans, useTranslation } from 'react-i18next';

import { IndicatorTiersEnum } from '@/api/rest/resources/types/IndicatorProduct';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { Tier } from '@/components/widgets/o/IndicatorLibraryCard/IndicatorLibraryCard';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';

import { PortfolioPageLayout } from '../../components/PortfolioPageLayout';
import { ScenarioDetails } from './components/ScenarioDetails';
import { ScenarioSelector } from './components/ScenarioSelector';
import { BOOK_CONSULTATION_CALENDLY_LINK, NRP_METHODOLOGY_URL } from './constants';
import { EditScenarioContext, useCreateEditScenario } from './hooks/useEditScenario';

export const ScenarioForecastsPage = () => {
  const { t } = useTranslation();
  const isStaff = useIsStaffUser();

  return (
    <PortfolioPageLayout title={t('navbar.scenarioModelling')}>
      {isStaff ? <ScenarioPage /> : <ScenarioLabPromoOverlay />}
    </PortfolioPageLayout>
  );
};

export const ScenarioPage = ({
  activeTab = 'physical-risk',
  isDemo = false,
}: {
  activeTab?: string;
  isDemo?: boolean;
}) => {
  const editScenario = useCreateEditScenario({ isDemo });

  return (
    <EditScenarioContext.Provider value={editScenario}>
      <Stack className='flex flex-row gap-8'>
        <ScenarioSelector />
        <ScenarioDetails activeTab={activeTab} />
      </Stack>
    </EditScenarioContext.Provider>
  );
};

const ScenarioLabPromoOverlay = () => {
  const { t } = useTranslation();
  return (
    <div className='relative'>
      <div className='pointer-events-none select-none opacity-40 blur-xs' aria-hidden='true'>
        <ScenarioPage isDemo={true} activeTab='financial-impact' />
      </div>

      <Stack className='absolute top-1/2 inset-0 flex items-center justify-center p-2'>
        <Stack className='relative bg-white-100 rounded-2xl border border-neutral-black-12 shadow-lg p-8 max-w-6xl w-full'>
          <Tier tier={IndicatorTiersEnum.add_on} className='mt-[-34px]' />

          <Stack className='absolute top-4 right-4 flex flex-row items-center gap-1.5 rounded-md bg-neutral-black-8 px-2.5 py-1 typography-overline text-text-secondary'>
            <Icon icon='ri-computer-line' className='text-xs' />
            {t('global.misc.demoOnly')}
          </Stack>

          <Stack className='flex flex-col items-start gap-6 mt-8'>
            <span className='typography-h2 text-primary-100'>{t('shared.scenarioAnalysis.demoPreview.header')}</span>
            <span className='typography-body1 text-text-secondary'>
              {t('shared.scenarioAnalysis.demoPreview.description')}
            </span>
            <ButtonLink to={BOOK_CONSULTATION_CALENDLY_LINK} target='_blank'>
              {t('global.misc.bookConsultation')}
            </ButtonLink>
            <Trans
              i18nKey='shared.scenarioAnalysis.demoPreview.methodologyLink'
              components={{
                p: <p className='typography-body1 text-text-secondary' />,
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                Link: <a className='underline' href={NRP_METHODOLOGY_URL} target='_blank' rel='noreferrer' />,
              }}
            />
          </Stack>
        </Stack>
      </Stack>
    </div>
  );
};
